import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './IndustrySelector.css';

// Private business industries from industry_multiples table (rows 165-277)
const PRIVATE_INDUSTRIES = [
  "Car Dealerships",
  "Car Washes",
  "Equipment Rental and Dealers",
  "Gas Stations",
  "Junk and Salvage Yards",
  "Marine/Boat Service and Dealers",
  "Towing Companies",
  "Truck Stops",
  "Hair Salons and Barber Shops",
  "Massage Businesses",
  "Nail Salons",
  "Spas",
  "Tanning Salons",
  "Building Material and Hardware Stores",
  "Concrete Businesses",
  "Electrical and Mechanical Contracting Businesses",
  "HVAC Businesses",
  "Heavy Construction Businesses",
  "Plumbing Businesses",
  "Magazines and Newspapers",
  "Production Companies",
  "Day Care and Child Care Centers",
  "Preschools",
  "Schools",
  "Art Galleries",
  "Banquet Halls",
  "Bowling Alleys",
  "Casinos",
  "Golf Courses and Services",
  "Marinas and Fishing",
  "Nightclubs and Theaters",
  "Accounting and Tax Practices",
  "Banking and Loan Businesses",
  "Check Cashing Businesses",
  "Insurance Agencies",
  "Bakeries",
  "Bars, Pubs and Taverns",
  "Breweries",
  "Coffee Shops and Cafes",
  "Donut Shops",
  "Food Trucks",
  "Ice Cream and Frozen Yogurt Shops",
  "Juice Bars",
  "Restaurants",
  "Assisted Living and Nursing Homes",
  "Dance, Pilates, and Yoga Studios",
  "Dental Practices",
  "Gyms and Fitness Centers",
  "Home Health Care Businesses",
  "Medical Practices",
  "Auto, Boat and Aircraft Manufacturers",
  "Chemical and Related Product Manufacturers",
  "Clothing and Fabric Manufacturers",
  "Electronic and Electrical Equipment Manufacturers",
  "Energy and Petroleum Production Businesses",
  "Food and Related Product Manufacturers",
  "Furniture and Fixture Manufacturers",
  "Glass, Stone, and Concrete Manufacturers",
  "Industrial and Commercial Machinery Manufacturers",
  "Lumber and Wood Products Manufacturers",
  "Machine Shops and Tool Manufacturers",
  "Medical Device and Product Manufacturers",
  "Metal Product Manufacturers",
  "Packaging Businesses",
  "Paper Manufacturers and Printing Businesses",
  "Rubber and Plastic Products Manufacturers",
  "Sign Manufacturers and Businesses",
  "Cell Phone and Computer Repair and Service Businesses",
  "Graphic and Web Design Businesses",
  "IT and Software Service Businesses",
  "Software and App Companies",
  "Websites and Ecommerce Businesses",
  "Dog Daycare and Boarding Businesses",
  "Pet Grooming Businesses",
  "Pet Stores and Supply Businesses",
  "Bike Shops",
  "Clothing and Accessory Stores",
  "Convenience Stores",
  "Dollar Stores",
  "Flower Shops",
  "Furniture and Furnishings Stores",
  "Grocery Stores and Supermarkets",
  "Health Food and Nutrition Businesses",
  "Jewelry Stores",
  "Liquor Stores",
  "Nursery and Garden Centers",
  "Pawn Shops",
  "Pharmacies",
  "Smoke Shops",
  "Vending Machine Businesses",
  "Architecture and Engineering Firms",
  "Catering Companies",
  "Cleaning Businesses",
  "Commercial Laundry Businesses",
  "Dry Cleaners",
  "Funeral Homes",
  "Landscaping and Yard Service Businesses",
  "Laundromats and Coin Laundry Businesses",
  "Legal Services and Law Firms",
  "Locksmith Businesses",
  "Medical Billing Businesses",
  "Pest Control Businesses",
  "Property Management Businesses",
  "Security Businesses",
  "Staffing Agencies",
  "Waste Management and Recycling Businesses",
  "Delivery, Freight and Service Routes",
  "Limo and Passenger Transportation Businesses",
  "Moving and Shipping Businesses",
  "Storage Facilities and Warehouses",
  "Trucking Companies",
  "Durable Goods Wholesalers and Distributors",
  "Nondurable Goods Wholesalers and Distributors"
];

/**
 * IndustrySelector Component
 *
 * Searchable dropdown for selecting business industry from private business list
 */
const IndustrySelector = ({ value, onChange, required = false }) => {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredIndustries, setFilteredIndustries] = useState(PRIVATE_INDUSTRIES);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter industries based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredIndustries(PRIVATE_INDUSTRIES);
    } else {
      const filtered = PRIVATE_INDUSTRIES.filter(industry =>
        industry.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredIndustries(filtered);
      setFocusedIndex(-1);
    }
  }, [searchTerm]);

  // Update search term when value prop changes
  useEffect(() => {
    if (value) {
      setSearchTerm(value);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    onChange(newValue);
  };

  const handleSelectIndustry = (industry) => {
    setSearchTerm(industry);
    onChange(industry);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!isOpen && e.key !== 'Escape') {
      setIsOpen(true);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev =>
          prev < filteredIndustries.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev =>
          prev > 0 ? prev - 1 : filteredIndustries.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredIndustries.length) {
          handleSelectIndustry(filteredIndustries[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  return (
    <div className="industry-selector" ref={dropdownRef}>
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="Type to search industries..."
        className="industry-input"
        required={required}
        autoComplete="off"
      />

      {isOpen && filteredIndustries.length > 0 && (
        <div className="industry-dropdown">
          {filteredIndustries.map((industry, index) => (
            <div
              key={industry}
              className={`industry-option ${index === focusedIndex ? 'focused' : ''} ${searchTerm === industry ? 'selected' : ''}`}
              onClick={() => handleSelectIndustry(industry)}
              onMouseEnter={() => setFocusedIndex(index)}
            >
              {industry}
            </div>
          ))}
        </div>
      )}

      {isOpen && filteredIndustries.length === 0 && searchTerm.trim() !== '' && (
        <div className="industry-dropdown">
          <div className="industry-no-results">
            No industries found matching "{searchTerm}"
          </div>
        </div>
      )}
    </div>
  );
};

IndustrySelector.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool
};

export default IndustrySelector;
